import { useState, useEffect } from "react";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { X, ArrowRight, Shield, Code, Cpu, Bot } from "lucide-react";
import { useNavigate } from "react-router-dom";
import dean from "./images/Dean.webp";
import principal from "./images/Principal.webp";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SocialButtons from "../HomePage/SocialButtons";
import ApplyButton from "../common/ApplyButton";
import FeeDataComponent from "../FeeData/FeeDataComponent";
import NotificationModal from "./NotificationModal";

const AnimatedSection = ({ children }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      animate={controls}
      initial="hidden"
      transition={{ duration: 0.5, ease: "easeOut" }}
      variants={{
        visible: { opacity: 1, y: 0 },
        hidden: { opacity: 0, y: 50 },
      }}
    >
      {children}
    </motion.div>
  );
};

const VirtualTourModal = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.9 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0.9 }}
      className="relative w-full max-w-4xl aspect-video"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
      >
        <X size={24} />
      </button>
      <iframe
        width="100%"
        height="100%"
        src="https://www.youtube.com/embed/vKfPW8v3MHA?si=H0-GQtqiXLUJHiyy"
        title="Virtual Campus Tour"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      ></iframe>
    </motion.div>
  </motion.div>
);

const HomePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showVirtualTour, setShowVirtualTour] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  // const [showNotifications, setShowNotifications] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Simulate loading delay
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000); // 2 seconds loading time, adjust as needed

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    // Show notifications modal after a short delay when the page loads
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 1000); // 1 second delay after page load

    return () => clearTimeout(timer);
  }, []);

  // Disable background scroll when modal is open, enable when closed
  useEffect(() => {
    if (isOpen == true) {
      // Disable scroll
      document.body.style.overflow = "hidden";
      // Hide the background by adding a class to the body or using inline styles
      // document.body.style.pointerEvents = "none";
      // Optionally, you can also add a class to the background (e.g., <div className="backdrop"></div>) to visually disable it.
    } else {
      // Restore scroll and pointer events
      document.body.style.overflow = "";
      document.body.style.pointerEvents = "";
    }

    // Cleanup on component unmount or when modal is closed
    return () => {
      document.body.style.overflow = ""; // Restore scroll if modal unmounts
      document.body.style.pointerEvents = ""; // Restore pointer events
    };
  }, [isOpen]);

  // useEffect(() => {
  //   async function fetchAnnouncements() {
  //     try {
  //       setIsLoading(true);
  //       const response = await fetch(
  //         `${process.env.REACT_APP_API_URL}/announcements/fetch-announcements`
  //       );

  //       if (!response.ok) {
  //         throw new Error("Failed to fetch announcements");
  //       }

  //       const data = await response.json();
  //       // Clear any previous errors
  //     } catch (err) {
  //       console.error("Error fetching announcements:", err);
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   }

  //   fetchAnnouncements();
  // }, []); // Fetch new announcements whenever the modal is opened

  const gallerySettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  // if (isLoading) {
  //   return (
  //     <div className="fixed inset-0 bg-blue-900 flex items-center justify-center z-50">
  //       <motion.div
  //         animate={{
  //           rotate: 360,
  //           transition: {
  //             duration: 1,
  //             repeat: Number.POSITIVE_INFINITY,
  //             ease: "linear",
  //           },
  //         }}
  //       >
  //         <Loader2 size={48} className="text-white" />
  //       </motion.div>
  //     </div>
  //   );
  // }

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Navbar />
      <FeeDataComponent />

      <main>
        {/* Hero Section */}
        <section className="relative h-screen flex items-center justify-center overflow-hidden">
          <video
            autoPlay
            loop
            muted
            className="absolute w-auto min-w-full min-h-full max-w-none object-cover"
          >
            <source
              // src="https://assets.mixkit.co/videos/preview/mixkit-set-of-plateaus-seen-from-the-heights-in-a-sunset-26070-large.mp4"
              src="/media/home-page/video/niit-video.mp4"
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-indigo-900/80"></div>
          <div className="relative z-10 text-center max-w-4xl px-6">
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="mb-6 inline-block"
            >
              <span className="px-4 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium">
                Shaping Tomorrow's Technology Leaders
              </span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight"
            >
              Welcome to <span className="text-blue-300">NIIT</span>
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="flex items-center justify-center mb-6"
            >
              <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full border border-white/30">
                <img
                  src="/images/home-page/air-logo-transpatrent.png"
                  alt="Air University Logo"
                  className="h-10 w-auto mr-3"
                />
                <p className="text-white font-medium">
                  A Constituent College of Air University, Islamabad
                </p>
              </div>
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="text-xl md:text-2xl text-gray-200 mb-10 max-w-2xl mx-auto"
            >
              Empowering students with cutting-edge technology education and
              innovative research opportunities since 2000.
            </motion.p>
            {/* <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 1 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <button
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/30 px-8 py-4 rounded-full text-lg font-semibold transition-colors duration-300"
                onClick={() => setShowVirtualTour(true)}
              >
                Virtual Tour
              </button>
            </motion.div> */}
          </div>
          <div className="absolute bottom-10 left-0 right-0 flex justify-center">
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Number.POSITIVE_INFINITY, duration: 1.5 }}
              className="w-8 h-12 border-2 border-white/50 rounded-full flex justify-center pt-2"
            >
              <motion.div
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ repeat: Number.POSITIVE_INFINITY, duration: 1.5 }}
                className="w-1 h-3 bg-white rounded-full"
              />
            </motion.div>
          </div>
        </section>

        {/* Leadership Messages Section - Clean Professional Design */}
        <AnimatedSection>
          <section className="py-16 bg-gradient-to-b from-blue-900 to-blue-950 relative">
            <div className="container mx-auto px-4 relative z-10">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Leadership
                </h2>
                <div className="w-20 h-1 bg-blue-500 mx-auto"></div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
                {/* Principal's Message */}
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <div className="bg-blue-800 p-4">
                    <h3 className="text-xl font-bold text-white text-center">
                      Message from the Principal
                    </h3>
                  </div>

                  <div className="p-6">
                    <div className="flex flex-col md:flex-row">
                      <div className="md:w-1/3 mb-4 md:mb-0 flex justify-center">
                        <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-blue-100 shadow-md">
                          <img
                            src={principal || "/placeholder.svg"}
                            alt="Principal"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>

                      <div className="md:w-2/3 md:pl-6">
                        <div className="mb-4 bg-blue-50 p-5 rounded-lg border-l-4 border-blue-500">
                          <p className="text-gray-700 italic">
                            "At NASTP, we are dedicated to nurturing innovation,
                            critical thinking, and technological proficiency.
                            Our mission is to equip students with the skills and
                            mindset needed to not only excel in today's
                            workforce but to also thrive in the evolving
                            landscape of tomorrow's challenges and
                            opportunities."
                          </p>
                        </div>

                        <div className="text-center md:text-left">
                          <p className="text-lg font-bold text-blue-800">
                            Air Commodore Nadir Munir (Retd)
                          </p>
                          <p className="text-sm text-gray-600">
                            Principal, NASTP Institute of Information Technology
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dean's Message */}
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <div className="bg-teal-800 p-4">
                    <h3 className="text-xl font-bold text-white text-center">
                      Message from the Dean
                    </h3>
                  </div>

                  <div className="p-6">
                    <div className="flex flex-col md:flex-row">
                      <div className="md:w-1/3 mb-4 md:mb-0 flex justify-center">
                        <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-teal-100 shadow-md">
                          <img
                            src={dean || "/placeholder.svg"}
                            alt="Dean"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>

                      <div className="md:w-2/3 md:pl-6">
                        <div className="mb-4 bg-teal-50 p-5 rounded-lg border-l-4 border-teal-500">
                          <p className="text-gray-700 italic">
                            "NIIT is not just an institution; it is a dynamic
                            community of innovators, thinkers, and future
                            leaders. We focus on practical, hands-on learning
                            that empowers our graduates with the knowledge and
                            skills needed to excel in the fast-paced and
                            ever-changing world of technology."
                          </p>
                        </div>

                        <div className="text-center md:text-left">
                          <p className="text-lg font-bold text-teal-800">
                            Prof. DR. M. ALMAS ANJUM SI(M)
                          </p>
                          <p className="text-sm text-gray-600">
                            Dean, NASTP Institute of Information Technology
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* About Section */}
        <AnimatedSection>
          <section className="py-24 bg-white">
            <div className="container mx-auto px-6">
              <div className="flex flex-col md:flex-row items-center max-w-6xl mx-auto">
                <div className="md:w-1/2 mb-12 md:mb-0">
                  <motion.div
                    className="relative rounded-2xl overflow-hidden shadow-sm " //shadow-2xl
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="/images/home-page/about-niit.jpg"
                      alt="NASTP Campus"
                      className="w-full h-auto rounded-2xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent"></div>
                    {/* <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg">
                      <div className="text-sm font-medium text-gray-500">
                        Established
                      </div>
                      <div className="text-xl font-bold text-blue-700">
                        2023
                      </div>
                    </div> */}
                  </motion.div>
                </div>
                <div className="md:w-1/2 md:pl-16">
                  <div className="inline-block px-3 py-1 bg-blue-100 rounded-full text-blue-700 text-sm font-medium mb-6">
                    About NASTP
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    A Premier Institution for <br className="hidden md:block" />
                    <span className="text-blue-700">Technology Education</span>
                  </h2>
                  <p className="text-gray-700 text-md mb-8 leading-relaxed">
                    The NASTP Institute of Information Technology (NIIT), a
                    constituent college of Air University, offers programs in
                    Computer Science, AI, Software Engineering, and Cyber
                    Security. Located in the National Aerospace Science and
                    Technology Park (NASTP), NIIT combines a cutting-edge
                    curriculum, expert faculty, and modern facilities to prepare
                    graduates for leadership in the digital economy.
                  </p>
                  {/* <div className="grid grid-cols-2 gap-6 mb-8">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 bg-blue-100 rounded-full p-3 mr-4">
                        <Users className="w-6 h-6 text-blue-700" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">5000+</div>
                        <div className="text-sm text-gray-600">
                          Alumni in tech
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="flex-shrink-0 bg-blue-100 rounded-full p-3 mr-4">
                        <BookOpen className="w-6 h-6 text-blue-700" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">100+</div>
                        <div className="text-sm text-gray-600">
                          Industry partners
                        </div>
                      </div>
                    </div>
                  </div> */}
                  <button
                    target="_blank"
                    onClick={() => {
                      navigate("/campus");
                    }}
                    className="px-8 py-3 bg-blue-700 text-white rounded-full text-lg font-semibold hover:bg-blue-800 transition-colors shadow-md hover:shadow-lg duration-300 flex items-center"
                  >
                    Campus Life <ArrowRight className="ml-2 w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Programs Section */}
        <AnimatedSection>
          <section className="py-24 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <div className="inline-block px-3 py-1 bg-blue-100 rounded-full text-blue-700 text-sm font-medium mb-6">
                  Academic Excellence
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Our Cutting-Edge Programs
                </h2>
                <p className="text-lg text-gray-600">
                  Discover our innovative programs designed to prepare you for
                  the rapidly evolving technology landscape.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
                {[
                  {
                    title: "Computer Science",
                    icon: <Code className="w-6 h-6 text-blue-700" />,
                    color: "bg-blue-50",
                    border: "border-blue-200",
                    description:
                      "Learn algorithms, data structures, and software development principles.",
                    navigation: "bscs",
                  },
                  {
                    title: "Software Engineering",
                    icon: <Cpu className="w-6 h-6 text-indigo-700" />,
                    color: "bg-indigo-50",
                    border: "border-indigo-200",
                    description:
                      "Master the art of building robust, scalable software systems.",
                    navigation: "bsse",
                  },
                  {
                    title: "Artificial Intelligence",
                    icon: <Bot className="w-6 h-6 text-purple-700" />,
                    color: "bg-purple-50",
                    border: "border-purple-200",
                    description:
                      "Explore machine learning, neural networks, and AI applications.",
                    navigation: "/bsai",
                  },
                  {
                    title: "Cyber Security",
                    icon: <Shield className="w-6 h-6 text-red-700" />,
                    color: "bg-red-50",
                    border: "border-red-200",
                    description:
                      "Develop skills to protect systems from digital threats and vulnerabilities.",
                    navigation: "/bscys",
                  },
                ].map((program, index) => (
                  <motion.div
                    key={program.title}
                    className={`${program.color} border ${program.border} rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group`}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="p-6">
                      <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-6 bg-white shadow-sm">
                        {program.icon}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-700 transition-colors">
                        {program.title}
                      </h3>
                      <p className="text-gray-600 mb-6">
                        {program.description}
                      </p>
                      {/* <a
                        href={program.navigation}
                        className="inline-flex items-center text-blue-700 font-medium hover:text-blue-800"
                      >
                        Learn more <ChevronRight className="ml-1 w-4 h-4" />
                      </a> */}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Campus Life Section */}
        <AnimatedSection>
          <section className="py-24 bg-blue-900">
            <div className="container mx-auto px-6">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <div className="inline-block px-3 py-1 bg-blue-800 rounded-full text-blue-200 text-sm font-medium mb-6">
                  Student Experience
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Experience Campus Life
                </h2>
                <p className="text-lg text-blue-200">
                  Discover the vibrant community and exceptional facilities that
                  make NIIT a unique place to learn and grow.
                </p>
              </div>

              <div className="max-w-6xl mx-auto">
                <Slider {...gallerySettings} className="campus-life-slider">
                  {[
                    {
                      img: "/images/home-page/Experience/welcome.JPG",
                      title: "Welcome Party",
                      description:
                        "A formal introduction to campus life and new connections.",
                    },

                    {
                      img: "images/home-page/Experience/softtech.png",
                      title: "Softtech Event",
                      description:
                        "Transform innovative ideas into practical solutions through collaboration and technology.",
                    },
                    {
                      img: "images/home-page/Experience/_DSC7603.JPG",
                      title: "Fire Drill",
                      description:
                        "Enhancing campus safety through regular emergency preparedness and evacuation training.",
                    },
                  ].map((item, index) => (
                    <div key={index} className="px-3 py-4">
                      <motion.div
                        className="relative overflow-hidden rounded-xl shadow-lg group"
                        whileHover={{ scale: 1.03 }}
                        transition={{ duration: 0.3 }}
                      >
                        <img
                          src={item.img || "/placeholder.svg"}
                          alt={item.title}
                          className="w-full h-72 object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-blue-900/50 to-transparent transition-opacity group-hover:opacity-90"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                          <h3 className="text-xl font-bold mb-2">
                            {item.title}
                          </h3>
                          <p className="text-blue-200 mb-4">
                            {item.description}
                          </p>
                          {/* <div className="transform translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                            <a
                              href="#"
                              className="inline-flex items-center text-white bg-blue-700/80 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium"
                            >
                              Explore <ArrowRight className="ml-1 w-4 h-4" />
                            </a>
                          </div> */}
                        </div>
                      </motion.div>
                    </div>
                  ))}
                </Slider>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Student Testimonials */}
        <AnimatedSection>
          <section className="py-24 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <div className="inline-block px-3 py-1 bg-blue-100 rounded-full text-blue-700 text-sm font-medium mb-6">
                  Student Voices
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  What Our Students Say
                </h2>
                <p className="text-lg text-gray-600">
                  Hear from our students about their experiences at NASTP.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {[
                  {
                    name: "Ibrahim Ansari",
                    program: "Artificial Intelligence",
                    quote:
                      "Studying at NIIT has been a truly inspiring journey. The dynamic academic environment and dedicated faculty have strengthened my foundation in the field. I feel empowered with knowledge and ready to tackle greater academic and professional challenges ahead.",
                    image: "/images/home-page/students/Ibrahim-Ansari-BSAI.jpg",
                  },
                  {
                    name: "RIDA FATIMA",
                    program: "Computer Science",
                    quote:
                      "In just a few semesters, NIIT has given me incredible exposure to real-world projects and emerging technologies. The skills and mentorship I'm gaining are shaping me into a confident professional, fully prepared to achieve my future career goals.",
                    image: "/images/home-page/students/RIDA-FATIMA-BSCS.png",
                  },
                  {
                    name: "MUAZ AHMAD",
                    program: "Computer Science",
                    quote:
                      "Being a part of NIIT has accelerated my learning journey. Through practical assignments and industry-relevant training, I am developing the skills I need to excel. I am excited about the opportunities that lie ahead in my career.",
                    image: "/images/home-page/students/MUAZ-AHMAD-BSCS.jpg",
                  },
                ].map((testimonial, index) => (
                  <motion.div
                    key={index}
                    className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden"
                    whileHover={{ y: -5 }}
                  >
                    <div className="p-6">
                      <div className="flex items-center mb-6">
                        <div className="w-20 h-20 rounded-full overflow-hidden mr-4">
                          <img
                            src={testimonial.image || "/placeholder.svg"}
                            alt={testimonial.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">
                            {testimonial.name}
                          </div>
                          <div className="text-sm text-blue-700">
                            {testimonial.program}
                          </div>
                        </div>
                      </div>
                      <div className="relative pl-6 border-l-2 border-blue-200 mb-4">
                        <p className="text-gray-700 italic">
                          "{testimonial.quote}"
                        </p>
                      </div>
                      <div className="flex items-center text-yellow-500">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className="w-4 h-4 fill-current"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Call to Action */}
        <AnimatedSection>
          <section className="py-24 bg-gradient-to-r from-blue-800 to-indigo-900 relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -right-40 -top-40 w-80 h-80 rounded-full bg-blue-600/20 blur-3xl"></div>
              <div className="absolute -left-20 -bottom-40 w-80 h-80 rounded-full bg-indigo-600/20 blur-3xl"></div>
            </div>
            <div className="container mx-auto px-6 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 leading-tight">
                  Ready to Shape Your Future in Technology?
                </h2>
                <p className="text-xl text-blue-200 mb-10 max-w-2xl mx-auto">
                  Join NIIT and embark on a journey of innovation, learning, and
                  growth. Applications for the next academic year are now open.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  {/* <motion.button
                    target="_blank"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      navigate("/login");
                    }}
                    className="bg-white text-blue-800 px-8 py-4 rounded-full text-lg font-semibold hover:bg-blue-50 transition-colors duration-300 shadow-lg"
                  >
                    Apply Now
                  </motion.button> */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ApplyButton
                      to="/login"
                      className="bg-white text-blue-800 px-8 py-4 rounded-full text-lg font-semibold hover:bg-blue-50 transition-colors duration-300 shadow-lg"
                    >
                      Apply Now
                    </ApplyButton>
                  </motion.div>
                </div>
              </div>
            </div>
          </section>
        </AnimatedSection>
      </main>

      {/* Footer */}
      <Footer />
      <AnimatePresence>
        {showVirtualTour && (
          <VirtualTourModal onClose={() => setShowVirtualTour(false)} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isOpen && (
          <NotificationModal
            onClose={() => setIsOpen(false)}
            isOpen={isOpen}
            // notifications={notifications}
          />
        )}
      </AnimatePresence>

      {/* social buttons */}
      <SocialButtons />
    </div>
  );
};

export default HomePage;
